<template>
  <div class="modal-wrapper">
    <div class="modal-header flex items-center justify-between">
      <div>Container: {{ source_id }}</div>
      <div class="flex items-center gap-1">
        <i
          v-if="!$parent.clickToClose"
          class="el-icon-close"
          @click="$emit('close')"
        ></i>
      </div>
    </div>
    <div class="modal-content flex flex-col gap-1">
      <fieldset class="border" v-if="streetTurn.connected_id">
        Already street turn to the order
        <popup
          componentPath="components/html"
          width="900px"
          :pivot-y="0"
          :options="viewOrderOptions(streetTurn.connected_id)"
          >{{ streetTurn.connected_id }}</popup
        >. Click <a @click="destroy" class="red">unlink</a> to disconnect street
        turn.
      </fieldset>
      <slot v-else>
        <el-alert
          v-if="errors"
          :title="errors"
          type="error"
          show-icon
          :closable="false"
          style="margin-bottom: 5px"
        />
        <div class="e-form pb-1">
          <el-form inline label-position="top" size="mini">
            <el-form-item label="Street Turn">
              <el-select
                v-model="streetTurn.type"
                filterable
                @change="fetchLocations"
              >
                <el-option
                  v-for="item in options.types"
                  :key="item.name"
                  :label="item.title"
                  :value="item.name"
                >
                </el-option>
              </el-select>
            </el-form-item>
            <el-form-item label="To Cont. ID" class="relative">
              <el-autocomplete
                ref="ordersAutocomplete"
                popper-class="street-turn-orders"
                v-model="streetTurn.dest_id"
                clearable
                placeholder="ID, Cont. #, Refer #"
                :debounce="500"
                :fetch-suggestions="queryOrders"
                @select="selectOrder"
                @blur="handleDestOrder"
                @clear="handleDestOrder"
                style="width: 150px"
              >
                <template slot-scope="{ item }">
                  <div
                    :id="`order-${item.id}`"
                    :class="{ current: item.id == streetTurn.dest_id }"
                  >
                    <div class="flex items-center justify-between px-1">
                      <div class="font-bold">
                        <div class="flex items-center gap-1">
                          {{ item.abbrev_type }} {{ item.id }}
                          <span class="f90">{{ item.trucker_names[0] }}</span>
                        </div>
                      </div>
                      <div
                        class="suggestion-tags flex items-center gap-0.5"
                        @mousedown.stop.prevent="selectSuggestionText"
                        @click.stop
                      >
                        <div
                          v-if="item.abbrev_type == 'IM'"
                          class="tag mini w-20"
                        >
                          PO: {{ item.pickup_no }}
                        </div>
                        <div
                          v-if="item.reference_no"
                          class="tag mini w-32 overflow-auto"
                        >
                          REF: {{ item.reference_no }}
                        </div>
                        <div v-if="item.container_no" class="tag mini w-20">
                          {{ item.container_no }}
                        </div>
                        <div class="w-16 text-xs font-bold">
                          {{ item.appt_date_fmt.md }}
                          <sup>
                            {{ item.appt_date_fmt.weekday }}
                          </sup>
                        </div>
                        <i
                          class="el-icon-success"
                          :class="{
                            black: item.appt_confirmed,
                            gray5: !item.appt_confirmed,
                          }"
                        ></i>
                      </div>
                    </div>
                    <div class="locations grid grid-cols-2 gap-1">
                      <div class="truncate">
                        {{ companyName(item, ["Depot", "Terminal"]) }}
                      </div>
                      <div class="truncate">
                        {{ companyName(item, ["Warehouse"]) }}
                      </div>
                    </div>
                  </div>
                </template>
              </el-autocomplete>
              <popup
                v-if="isOrderId(streetTurn.dest_id)"
                componentPath="components/html"
                width="900px"
                :pivot-y="0"
                :options="viewOrderOptions(streetTurn.dest_id)"
              >
                <a class="el-icon-view absolute top-2 right-5" />
              </popup>
              <div
                v-if="destIdWarning"
                class="text-2xs bg-red-500 px-0.5 text-white absolute top-6 left-0 w-full"
                >{{ destIdWarning }}</div
              >
            </el-form-item>
            <el-form-item label=" ">
              <el-checkbox
                v-model="streetTurn.straight"
                class="mini"
                @change="fetchLocations"
                :disabled="!streetTurn.direct_reuse"
              >
                Direct Reuse
                <el-tooltip
                  content="Only allow to direct reuse if the last operation is not 'Chassis Return'"
                  placement="bottom"
                  v-if="!streetTurn.direct_reuse"
                >
                  <i class="el-icon-question red fs15"></i>
                </el-tooltip>
              </el-checkbox>
            </el-form-item>
            <el-form-item label=" " v-if="isDualAllowed">
              <el-checkbox v-model="streetTurn.dual" class="mini">
                Dual
                <el-tooltip
                  content="Sync next pickup location, appt date/time, and pickup instructions to the current empty return."
                  placement="bottom"
                >
                  <i class="el-icon-question gray5 fs15"></i>
                </el-tooltip>
              </el-checkbox>
            </el-form-item>
            <el-form-item label=" ">
              <el-checkbox
                v-model="streetTurn.trucker"
                class="mini"
                :disabled="truckerDisabled"
              >
                Same Trucker
              </el-checkbox>
            </el-form-item>
            <el-form-item label="At Location" class="w-72">
              <el-select
                filterable
                clearable
                v-model="streetTurn.location_id"
                class="w-full"
              >
                <el-option-group
                  v-for="(locations, name) in options.locations"
                  :key="name"
                  :label="name.toUpperCase()"
                >
                  <el-option
                    v-for="item in locations"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                    class="two-lines"
                  >
                    <div class="title-1">{{ item.name }}</div>
                    <div class="title-2">
                      {{ item.street }}, {{ item.city }}, {{ item.state }}
                      {{ item.zip }}
                    </div>
                  </el-option>
                </el-option-group>
              </el-select>
              <div class="hint">{{ displayAddress }}</div>
            </el-form-item>
            <el-form-item label=" ">
              <el-button type="primary" @click="create">Connect</el-button>
            </el-form-item>
            <div class="box" v-if="avantidaReady">
              <div class="box-title">Avantida Request</div>
              <div class="box-content flex flex-col gap-1">
                <div class="flex items-center gap-1" v-if="unlocodes.source.id">
                  <div class="el-input-inner-label">
                    <label class="w-48">
                      <b class="green">{{ streetTurn.source_id }}</b>
                      Port Unlocode
                    </label>
                    <unlocode
                      :id="unlocodes.source.id"
                      :string="unlocodes.source.port_unlocode"
                      :preload="unlocodes.source.port_unlocode_preload"
                      :autosave="true"
                    />
                  </div>
                  <div class="mx-1">>></div>
                  <div class="el-input-inner-label" v-if="unlocodes.dest.id">
                    <label class="w-48">
                      <b class="red">{{ streetTurn.dest_id }}</b> Port Unlocode
                    </label>
                    <unlocode
                      :id="unlocodes.dest.id"
                      :string="unlocodes.dest.port_unlocode"
                      :preload="unlocodes.dest.port_unlocode_preload"
                      :autosave="true"
                    />
                  </div>
                  <div v-else class="tag h-7 red"> Choose 'To Cont. ID' </div>
                  <div class="el-input-inner-label">
                    <label class="w-20">Reuse Date</label>
                    <el-date-picker
                      size="mini"
                      v-model="reuseDate"
                      type="datetime"
                      format="MM/dd HH:mm"
                      value-format="yyyy-MM-dd HH:mm"
                      placeholder="Required"
                      style="width: 120px"
                    />
                  </div>
                  <el-button
                    type="success"
                    size="mini"
                    :disabled="!reuseDate || !unlocodes.dest.id"
                    @click="requestReuse"
                  >
                    Request
                  </el-button>
                </div>
                <v-table
                  :items="avantidaReuseRequests"
                  :row-height="30"
                  :defaultSort="{ prop: 'created_at', order: 'descending' }"
                  :max-height="400"
                >
                  <template v-slot:tbody>
                    <u-table-column
                      prop="dest_container_id"
                      label="Target"
                      width="80"
                    >
                      <template v-slot="scope">
                        <popup
                          componentPath="components/html"
                          width="900px"
                          :pivot-y="0"
                          :options="
                            viewOrderOptions(scope.row.dest_container_id)
                          "
                          >{{ scope.row.dest_container_id }}</popup
                        >
                      </template>
                    </u-table-column>
                    <u-table-column
                      prop="requester_name"
                      label="Requester"
                      width="120"
                    />
                    <u-table-column
                      prop="reuse_date_display"
                      label="Reuse Date"
                      width="140"
                    />
                    <u-table-column
                      prop="sync_status"
                      label="Sync Status"
                      width="100"
                    />
                    <u-table-column prop="avantida_status" label="Status" />
                    <u-table-column
                      prop="validation_errors"
                      label="Validation Errors"
                    />
                    <u-table-column
                      prop="last_error"
                      label="Last Error"
                      width="240"
                    />
                    <u-table-column
                      prop="timestamp"
                      label="Requested"
                      width="140"
                    />
                  </template>
                </v-table>
              </div>
            </div>
          </el-form>
        </div>
      </slot>
      <fieldset class="border text-xs leading-relaxed">
        <b
          ><i class="fas fa-star" /> Steet turn chassis with container (Only
          support from import to export)</b
        >
        <ul style="margin-left: 5px">
          <li>
            1. Enter an export order keyword and select "Direct Reuse". The
            import order will street turn Container&Chassis directly to load
            shipper.
          </li>
          <li>
            2. Enter an export order keyword and unselect "Direct Reuse", choose
            Yard to return Container&Chassis where the driver will pickup next
            time to load shipper.
          </li>
          <li>
            <em>
              <span class="font-bold red">Note:</span> The Container&Chassis No.
              from the import order will be applied to the export order.</em
            >
          </li>
        </ul>
        <hr />
        <b>
          <i class="fas fa-star" /> Steet turn only chassis (Support between
          import and export)</b
        >
        <ul style="margin-left: 5px">
          <li>
            1. Enter an order keyword and select "Direct Reuse". The current
            order will move chassis directly to load a Container for the next
            order.
          </li>
          <li>
            2. Enter an order keyword and select "Direct Reuse", choose a
            Depot/Terminal. The current order will move chassis directly to new
            Depot/Terminal to load a Container for the next order.
          </li>
          <li>
            3. Enter an order keyword and unselect "Direct Reuse", choose a Yard
            to return Chassis which the driver will pickup next time to load a
            Container for the next order.
          </li>
          <li>
            4. On an import or IBI order, select "Direct Reuse" and "Dual" to
            sync the next pickup location, appt date/time, and pickup
            instructions to the current empty return.
          </li>
          <li>
            <em>
              <span class="font-bold red">Note:</span> The Chassis No. will be
              applied to the next order.</em
            >
          </li>
        </ul>
        <hr />
        <b>
          <i class="fas fa-star" />
          Circle streen turn is disallowed. (e.g. Container A street turn to B,
          then B street turn to A again.)
        </b>
      </fieldset>
    </div>
  </div>
</template>
<script>
import { format } from "date-fns";
import Container from "../../../controllers/container";
export default {
  name: "ContainerStreetTurn",
  props: ["source_id", "dest_id", "reload"],
  data() {
    return {
      streetTurn: {
        source_id: this.source_id,
        dest_id: this.dest_id,
      },
      options: {
        types: [],
        locations: {},
        orders: [],
      },
      unlocodes: {
        source: {},
        dest: {},
      },
      querying: false,
      errors: null,
      format,
      truckerDisabled: false,
      reuseDate: null,
    };
  },
  async mounted() {
    await this.init();
  },
  watch: {
    "streetTurn.type": function () {
      this.resetDual();
    },
    "streetTurn.straight": function (nv) {
      this.$set(this.streetTurn, "trucker", nv);
      this.truckerDisabled = nv;
      this.resetDual();
    },
    "streetTurn.dest_id": async function (nv, ov) {
      let orderDom = document.getElementById("order-" + nv);
      if (orderDom) orderDom.scrollIntoView();
    },
  },
  computed: {
    onlyChassis() {
      return this.streetTurn.type == "only_chassis";
    },
    isDualAllowed() {
      return (
        this.onlyChassis &&
        ["IM", "IBI"].includes(this.streetTurn.source_type) &&
        this.streetTurn.straight
      );
    },
    locationId() {
      return this.streetTurn.location_id;
    },
    flatLocations() {
      return Object.values(this.options.locations).flat();
    },
    displayAddress() {
      const loc = this.flatLocations.find((o) => o.id == this.locationId);
      return loc ? `${loc.street}, ${loc.city}, ${loc.state} ${loc.zip}` : null;
    },
    avantidaReady() {
      return this.streetTurn.avantida?.ready === true;
    },
    avantidaReuseRequests() {
      return [...(this.streetTurn.avantida?.requests || [])].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
    },
    isOrderId() {
      return function (value) {
        const id = (value || "").toString().trim();
        return /^\d+$/.test(id) && !id.startsWith("0");
      };
    },
    destIdWarning() {
      if (this.querying) return;
      if (this.isEmptyData(this.streetTurn.dest_id)) return;
      if (this.isOrderId(this.streetTurn.dest_id)) return;
      if (this.isEmptyData(this.options.orders)) {
        return "Retrieve orders with new input";
      } else {
        return "Choose an order from the list";
      }
    },
  },
  methods: {
    resetDual() {
      if (!this.isDualAllowed) this.$set(this.streetTurn, "dual", false);
    },
    async init() {
      try {
        const response = await this.$http.get("/api/v1/street_turns/new", {
          params: { street_turn: this.streetTurn },
        });
        const { types, ...rest } = response.data;
        this.streetTurn = rest;
        this.options.types = types;
        if (!this.streetTurn.connected_id) {
          await this.fetchLocations();
        }
        const { source_id, connected_id } = this.streetTurn;
        this.unlocodes.source = await this.fetchUnlocode(source_id);
        this.unlocodes.dest = await this.fetchUnlocode(connected_id);
      } catch (err) {
        this.errors = err.response.data.errors;
      }
    },
    async queryOrders(queryString, cb) {
      try {
        this.querying = true;
        const response = await this.$http.get("/api/v1/street_turns", {
          params: {
            source_id: this.source_id,
            dest_id: queryString,
          },
        });
        let orders = response.data;
        if (!this.onlyChassis)
          orders = orders.filter((o) => o.abbrev_type == "EX");
        this.options.orders = orders;
        this.querying = false;
        if (cb) cb(orders);
      } catch (err) {
        this.errors = err.response.data.errors;
        this.querying = false;
      }
    },
    selectOrder(item) {
      this.streetTurn.dest_id = item.id.toString();
      this.queueDestOrder();
    },
    handleDestOrder() {
      this.queueDestOrder();
      clearTimeout(this.clearSuggestionsTimer);
      this.clearSuggestionsTimer = setTimeout(() => {
        if (this.$refs.ordersAutocomplete) {
          this.$refs.ordersAutocomplete.suggestions = [];
        }
      }, 250);
    },
    queueDestOrder() {
      clearTimeout(this.destOrderTimer);
      if (!this.isOrderId(this.streetTurn.dest_id)) return;
      this.destOrderTimer = setTimeout(() => {
        this.fetchDestUnlocode();
      }, 250);
    },
    async fetchUnlocode(id) {
      try {
        if (this.isEmptyData(id)) return {};
        const response = await this.$http.get(
          `/api/v1/unlocodes/${id}/container`,
        );
        return response.data;
      } catch (err) {
        return {};
      }
    },
    async fetchDestUnlocode() {
      this.unlocodes.dest = await this.fetchUnlocode(this.streetTurn.dest_id);
    },
    async fetchLocations() {
      try {
        const response = await this.$http.get(
          "/api/v1/street_turns/locations",
          {
            params: { street_turn: this.streetTurn },
          },
        );
        this.options.locations = response.data;
      } catch (err) {
        this.errors = err.response.data.errors;
      }
    },
    async create() {
      try {
        this.errors = null;
        const { type, source_id, dest_id, straight, dual, location_id } =
          this.streetTurn;
        const response = await this.$http.post(`/api/v1/street_turns`, {
          street_turn: {
            type,
            source_id,
            dest_id,
            straight,
            dual,
            location_id,
          },
        });
        this.$message({ message: response.data.message, type: "success" });
        this.$emit("close");
        if (this.reload) Container.refresh(source_id);
      } catch (err) {
        this.errors = err.response.data.errors;
      }
    },
    async requestReuse() {
      if (!this.reuseDate) {
        this.$message({ message: "Reuse Date is required", type: "warning" });
        return;
      }
      const { type, source_id, dest_id, straight, location_id } =
        this.streetTurn;
      try {
        const response = await this.$http2.post(
          `/api/v1/street_turns/request_reuse`,
          {
            street_turn: {
              type,
              source_id,
              dest_id,
              straight,
              location_id,
              reuse_date: this.reuseDate,
            },
          },
        );
        this.addReuseRequest(response.data.reuse_request);
        this.$message({ message: response.data.message, type: "success" });
      } catch (err) {
        const data = err.response?.data || {};
        if (data.reuse_request) this.addReuseRequest(data.reuse_request);
      }
    },
    addReuseRequest(reuseRequest) {
      if (!reuseRequest || !this.avantidaReady) return;
      if (!this.streetTurn.avantida.requests) {
        this.$set(this.streetTurn.avantida, "requests", []);
      }
      this.streetTurn.avantida.requests.push(reuseRequest);
    },
    async destroy() {
      try {
        const response = await this.$http.delete(
          `/api/v1/street_turns/${this.streetTurn.source_id}`,
        );
        this.$message({ message: response.data.message, type: "success" });
        this.$emit("close");
        if (this.reload) Container.refresh(this.streetTurn.source_id);
      } catch (err) {
        this.errors = err.response.data.errors;
      }
    },
    selectSuggestionText(event) {
      const node = event.target.closest(".tag") || event.currentTarget;
      const range = document.createRange();
      range.selectNodeContents(node);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    },
    companyName(item, types) {
      const operation = item.operations.find((o) =>
        types.includes(o.company.type),
      );
      return operation
        ? `${operation.name} - ${operation.company.name} (${operation.company.city})`
        : null;
    },
  },
};
</script>
<style lang="scss">
.street-turn-orders {
  &.el-autocomplete-suggestion.el-popper {
    @apply w-[640px] #{!important};
    ul {
      @apply flex flex-col gap-1 m-1;
      li {
        @apply p-0 border border-gray-200 #{!important};
        .locations {
          @apply text-2xs leading-5 font-normal tracking-tight px-1 bg-sky-100;
        }
        .suggestion-tags {
          @apply select-text cursor-text;
        }
      }

      .current {
        @apply bg-yellow-100 border border-orange-500 #{!important};
      }
    }
  }
}
</style>
